/**
 * Agent function form component for creating and editing agent functions.
 * Follows the ToolForm pattern with Card/CardContent layout and sectioned sections.
 * Supports Mustache variable templating in System Prompt linked to Input Schema properties.
 * Handles complex JSON Schemas with $ref, oneOf, $defs, and nested objects.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DataObject as SectionIcon,
} from '@mui/icons-material';
import { CodeEditor } from '../common/CodeEditor';
import { agentFunctionService } from '../../services/agentFunctionService';
import { useNotificationStore } from '../../store/notificationStore';
import { apiClient } from '../../services/api';
import type { PagedResult } from '../../types/tenant';
import type { AgentDefinitionDto } from '../../types/agent';
import type { AgentFunctionDetailDto } from '../../types/agentFunction';

function generateSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/^-+|-+$/g, '');
}

/** Represents a variable extracted from a JSON Schema. */
interface SchemaVariable {
  path: string;
  type: string;
  isSection: boolean;
  description?: string;
}

/**
 * Recursively extracts all variable paths from a JSON Schema string.
 * Resolves $ref pointers, merges oneOf/anyOf options, and traverses nested objects.
 * Returns flat list of { path, type, isSection } up to 4 levels deep.
 */
function extractSchemaVariables(schemaJson: string): SchemaVariable[] {
  try {
    const rootSchema = JSON.parse(schemaJson);
    if (!rootSchema || typeof rootSchema !== 'object') return [];

    const variables: SchemaVariable[] = [];

    // Resolve a $ref pointer like "#/$defs/insurer" against the root schema
    function resolveRef(ref: string): Record<string, unknown> | null {
      if (!ref || !ref.startsWith('#/')) return null;
      const parts = ref.substring(2).split('/');
      let current: unknown = rootSchema;
      for (const part of parts) {
        if (!current || typeof current !== 'object') return null;
        current = (current as Record<string, unknown>)[part];
      }
      return (current as Record<string, unknown>) ?? null;
    }

    // Resolve a schema node: follow $ref if present
    function resolve(node: unknown): Record<string, unknown> | null {
      if (!node || typeof node !== 'object') return null;
      const obj = node as Record<string, unknown>;
      if (typeof obj.$ref === 'string') {
        return resolveRef(obj.$ref);
      }
      return obj;
    }

    // Collect all properties from a schema node, merging oneOf/anyOf options
    function collectProperties(node: unknown): Record<string, unknown> {
      const resolved = resolve(node);
      if (!resolved) return {};

      // Direct properties
      if (resolved.properties && typeof resolved.properties === 'object') {
        return { ...(resolved.properties as Record<string, unknown>) };
      }

      // oneOf/anyOf: merge properties from all variant options
      const merged: Record<string, unknown> = {};
      const options = (resolved.oneOf || resolved.anyOf) as unknown[] | undefined;
      if (Array.isArray(options)) {
        for (const option of options) {
          const optResolved = resolve(option);
          if (optResolved?.properties && typeof optResolved.properties === 'object') {
            Object.assign(merged, optResolved.properties as Record<string, unknown>);
          }
        }
      }
      return merged;
    }

    // Check if a schema node represents an object with child properties
    function hasChildProperties(node: unknown): boolean {
      const resolved = resolve(node);
      if (!resolved) return false;
      if (resolved.properties && typeof resolved.properties === 'object' &&
          Object.keys(resolved.properties as object).length > 0) return true;
      const options = (resolved.oneOf || resolved.anyOf) as unknown[] | undefined;
      if (Array.isArray(options)) {
        return options.some((opt) => {
          const r = resolve(opt);
          return r?.properties && typeof r.properties === 'object' &&
                 Object.keys(r.properties as object).length > 0;
        });
      }
      return false;
    }

    // Recursively extract variables from a schema node
    function extract(node: unknown, prefix: string, depth: number) {
      if (depth > 4) return;
      const properties = collectProperties(node);

      for (const [key, propNode] of Object.entries(properties)) {
        const path = prefix ? `${prefix}.${key}` : key;
        const resolvedProp = resolve(propNode);
        if (!resolvedProp) continue;

        const propType = (resolvedProp.type as string) || 'unknown';
        const isSection = hasChildProperties(propNode);

        variables.push({
          path,
          type: propType,
          isSection,
          description: (resolvedProp.description as string) || undefined,
        });

        // Recurse into nested objects / oneOf / anyOf
        if (isSection) {
          extract(propNode, path, depth + 1);
        }
      }
    }

    extract(rootSchema, '', 0);
    return variables;
  } catch {
    return [];
  }
}

/**
 * Extracts Mustache variable full paths from a template string.
 * Resolves section scope: inside {{#data}}...{{/data}}, {{name}} becomes "data.name".
 * Dot-notation variables like {{data.name}} are treated as absolute paths.
 */
function extractMustacheVariables(template: string): string[] {
  const variables = new Set<string>();
  const scopeStack: string[] = [];

  const pattern = /\{\{([#/^]?)([a-zA-Z_][a-zA-Z0-9_]*(?:\.[a-zA-Z_][a-zA-Z0-9_]*)*|\.)\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(template)) !== null) {
    const prefix = match[1]; // '#', '/', '^', or ''
    const varName = match[2];

    // Skip closing tags and the special {{.}} iterator
    if (prefix === '/') {
      scopeStack.pop();
      continue;
    }
    if (varName === '.') continue;

    // Build full path: dot-notation is absolute; plain names resolve relative to scope
    let fullPath: string;
    if (varName.includes('.')) {
      fullPath = varName;
    } else {
      const currentScope = scopeStack.length > 0 ? scopeStack[scopeStack.length - 1] : '';
      fullPath = currentScope ? `${currentScope}.${varName}` : varName;
    }

    variables.add(fullPath);

    // Opening section or inverted section: push scope
    if (prefix === '#' || prefix === '^') {
      scopeStack.push(fullPath);
    }
  }

  return Array.from(variables).sort();
}

export const AgentFunctionForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { addNotification } = useNotificationStore();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentDefinitions, setAgentDefinitions] = useState<{ id: string; name: string }[]>([]);

  // Form fields
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [agentDefinitionId, setAgentDefinitionId] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [inputSchemaJson, setInputSchemaJson] = useState('{}');
  const [outputSchemaJson, setOutputSchemaJson] = useState('{}');
  const [systemPrompt, setSystemPrompt] = useState('');

  const title = isEdit ? 'Edit Agent Function' : 'Create Agent Function';

  // Help dialog state
  const [helpOpen, setHelpOpen] = useState(false);

  // Track which variable groups are expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['']));

  // Deep-extract all variables from Input Schema (resolves $ref, oneOf, $defs)
  const schemaVariables = useMemo(() => extractSchemaVariables(inputSchemaJson), [inputSchemaJson]);

  // All schema variable paths (flat list for validation)
  const allSchemaPaths = useMemo(() => schemaVariables.map((v) => v.path), [schemaVariables]);

  // Extract Mustache variables used in the System Prompt (full dot-notation paths)
  const promptVariables = useMemo(() => extractMustacheVariables(systemPrompt), [systemPrompt]);

  // Variables in prompt that are NOT defined in the Input Schema
  const undefinedVariables = useMemo(
    () => promptVariables.filter((v) => !allSchemaPaths.includes(v)),
    [promptVariables, allSchemaPaths]
  );

  // Group schema variables by parent path for the chip UI
  const groupedVariables = useMemo(() => {
    const groups = new Map<string, SchemaVariable[]>();
    for (const v of schemaVariables) {
      const lastDot = v.path.lastIndexOf('.');
      const group = lastDot === -1 ? '' : v.path.substring(0, lastDot);
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(v);
    }
    return groups;
  }, [schemaVariables]);

  // Insert a {{variable}} tag into the system prompt
  const handleInsertVariable = (path: string) => {
    setSystemPrompt((prev) => {
      const tag = `{{${path}}}`;
      if (prev.trim().length === 0) return tag;
      return `${prev} ${tag}`;
    });
  };

  // Insert a {{#section}}...{{/section}} block into the system prompt
  const handleInsertSection = (path: string) => {
    setSystemPrompt((prev) => {
      const block = `{{#${path}}}\n\n{{/${path}}}`;
      if (prev.trim().length === 0) return block;
      return `${prev}\n${block}`;
    });
  };

  // Toggle a variable group expanded/collapsed
  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  // Load agent definitions for dropdown
  useEffect(() => {
    const loadAgentDefinitions = async () => {
      try {
        const res = await apiClient.get<PagedResult<AgentDefinitionDto>>('/agents?pageSize=100');
        setAgentDefinitions(
          (res.data?.items ?? []).map((a) => ({ id: a.id, name: a.name }))
        );
      } catch (err) {
        console.error('Failed to load agent definitions', err);
        addNotification('Failed to load agent definitions', 'error');
      }
    };
    loadAgentDefinitions();
  }, [addNotification]);

  // Load existing agent function in edit mode
  useEffect(() => {
    if (isEdit && id) {
      loadAgentFunction();
    }
  }, [id, isEdit]);

  const loadAgentFunction = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data: AgentFunctionDetailDto = await agentFunctionService.getAgentFunction(id);
      setName(data.name);
      setSlug(data.slug);
      setDescription(data.description ?? '');
      setAgentDefinitionId(data.agentDefinitionId ?? '');
      setIsActive(data.isActive ?? true);
      setInputSchemaJson(data.inputSchemaJson ?? '{}');
      setOutputSchemaJson(data.outputSchemaJson ?? '{}');
      setSystemPrompt(data.systemPrompt ?? '');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load agent function';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from name on create
  useEffect(() => {
    if (!isEdit) {
      const generated = generateSlug(name);
      if (generated) setSlug(generated);
    }
  }, [name, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!agentDefinitionId) {
      setError('Agent definition is required');
      return;
    }

    setSaving(true);
    try {
      if (isEdit && id) {
        await agentFunctionService.updateAgentFunction(id, {
          name: name.trim(),
          description: description.trim() || undefined,
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          systemPrompt: systemPrompt || '',
          isActive,
          agentDefinitionId,
        });
        addNotification('Agent function updated successfully', 'success');
        navigate(`/agent-functions/${id}`);
      } else {
        const created = await agentFunctionService.createAgentFunction({
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          inputSchemaJson: inputSchemaJson || '{}',
          outputSchemaJson: outputSchemaJson || '{}',
          systemPrompt: systemPrompt || '',
          isActive,
          agentDefinitionId,
        });
        addNotification('Agent function created successfully', 'success');
        navigate(`/agent-functions/${created.id}`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save agent function';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEdit && id) {
      navigate(`/agent-functions/${id}`);
    } else {
      navigate('/agent-functions');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
            {title}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Basic Info */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Basic Information
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 4,
                    mb: 4,
                  }}
                >
                  <TextField
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Slug"
                    value={slug}
                    onChange={(e) => !isEdit && setSlug(e.target.value)}
                    fullWidth
                    disabled={isEdit}
                    helperText={isEdit ? 'Slug cannot be changed when editing' : 'Auto-generated from name'}
                  />
                </Box>

                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                  sx={{ mb: 4 }}
                />

                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 240 }} required>
                    <InputLabel>Agent Definition</InputLabel>
                    <Select
                      value={agentDefinitionId}
                      label="Agent Definition"
                      onChange={(e) => setAgentDefinitionId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Select agent...</em>
                      </MenuItem>
                      {agentDefinitions.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                    label="Active"
                  />
                </Box>
              </Box>

              <Divider />

              {/* Input Schema */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Input Schema
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  JSON Schema for input validation.
                </Typography>
                <CodeEditor
                  value={inputSchemaJson}
                  onChange={setInputSchemaJson}
                  language="json"
                  height="220px"
                />
              </Box>

              <Divider />

              {/* Output Schema */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Output Schema
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  JSON Schema for output validation.
                </Typography>
                <CodeEditor
                  value={outputSchemaJson}
                  onChange={setOutputSchemaJson}
                  language="json"
                  height="220px"
                />
              </Box>

              <Divider />

              {/* System Prompt */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    System Prompt
                  </Typography>
                  <Tooltip title="Mustache variable syntax help">
                    <IconButton size="small" onClick={() => setHelpOpen(true)} color="primary">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Use <code>{'{{variable}}'}</code> for values, <code>{'{{#section}}...{{/section}}'}</code> for
                  nested objects. Click the help icon above for a full syntax guide.
                </Typography>

                {/* Grouped variable chips from Input Schema */}
                {groupedVariables.size > 0 && (
                  <Box
                    sx={{
                      mb: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.5,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1, fontWeight: 600 }}
                    >
                      Available variables (click to insert):
                    </Typography>

                    {Array.from(groupedVariables.entries()).map(([group, vars]) => (
                      <Box key={group || '__root'} sx={{ mb: 1 }}>
                        {/* Group header for nested sections */}
                        {group !== '' && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mb: 0.5,
                              cursor: 'pointer',
                            }}
                            onClick={() => toggleGroup(group)}
                          >
                            {expandedGroups.has(group) ? (
                              <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            ) : (
                              <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            )}
                            <Tooltip title={`Click chip to insert {{#${group}}}...{{/${group}}} section block`}>
                              <Chip
                                label={`${group} (section)`}
                                size="small"
                                icon={<SectionIcon sx={{ fontSize: 14 }} />}
                                color="secondary"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInsertSection(group);
                                }}
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem', cursor: 'pointer' }}
                              />
                            </Tooltip>
                          </Box>
                        )}

                        {/* Variable chips within this group */}
                        <Collapse in={group === '' || expandedGroups.has(group)}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 0.75,
                              ml: group ? 3.5 : 0,
                              mt: 0.5,
                            }}
                          >
                            {vars
                              .filter((v) => !v.isSection)
                              .map((v) => {
                                const isUsed = promptVariables.includes(v.path);
                                return (
                                  <Tooltip
                                    key={v.path}
                                    title={
                                      isUsed
                                        ? `"{{${v.path}}}" is used in the prompt`
                                        : `Click to insert "{{${v.path}}}" (${v.type})`
                                    }
                                  >
                                    <Chip
                                      label={`{{${v.path}}}`}
                                      size="small"
                                      icon={<CopyIcon sx={{ fontSize: 14 }} />}
                                      color={isUsed ? 'primary' : 'default'}
                                      variant={isUsed ? 'filled' : 'outlined'}
                                      onClick={() => handleInsertVariable(v.path)}
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        cursor: 'pointer',
                                      }}
                                    />
                                  </Tooltip>
                                );
                              })}
                            {/* Also show section-type children as insertable section chips */}
                            {vars
                              .filter((v) => v.isSection)
                              .map((v) => (
                                <Tooltip
                                  key={v.path}
                                  title={`Click to insert {{#${v.path}}}...{{/${v.path}}} section block`}
                                >
                                  <Chip
                                    label={`{{#${v.path}}}`}
                                    size="small"
                                    icon={<SectionIcon sx={{ fontSize: 14 }} />}
                                    color="secondary"
                                    variant="outlined"
                                    onClick={() => handleInsertSection(v.path)}
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.75rem',
                                      cursor: 'pointer',
                                    }}
                                  />
                                </Tooltip>
                              ))}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Warning: variables in prompt not defined in schema */}
                {undefinedVariables.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    The following variables are used in the System Prompt but not defined in the Input
                    Schema: {undefinedVariables.map((v) => `{{${v}}}`).join(', ')}. These will not be
                    resolved at execution time.
                  </Alert>
                )}

                <CodeEditor
                  value={systemPrompt}
                  onChange={setSystemPrompt}
                  language="plaintext"
                  height="200px"
                />
              </Box>

              {/* Mustache Syntax Help Dialog */}
              <Dialog
                open={helpOpen}
                onClose={() => setHelpOpen(false)}
                maxWidth="md"
                fullWidth
                scroll="paper"
              >
                <DialogTitle sx={{ fontWeight: 700 }}>
                  Mustache Variable Syntax Guide
                </DialogTitle>
                <DialogContent dividers>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Simple variable */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Simple Variable
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Insert a value directly from the input data.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
                        {'You are handling a {{entityType}} entity.'}
                      </Box>
                    </Box>

                    {/* Dot notation */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Dot Notation (Nested Properties)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Access nested object properties using dots.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
                        {'Contact email: {{data.contact.email}}\nCity: {{data.address.city}}'}
                      </Box>
                    </Box>

                    {/* Section blocks */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Section Blocks (Nested Objects)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Use <code>{'{{#section}}...{{/section}}'}</code> to scope into an object.
                        Inside the section, properties resolve relative to that object.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
{`{{#data}}
Entity: {{name}} (License: {{licenseNumber}})
Status: {{status}}
{{#contact}}
Email: {{email}} | Phone: {{phone}}
{{/contact}}
{{/data}}`}
                      </Box>
                    </Box>

                    {/* Conditionals */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Conditionals
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Sections also act as conditionals: content only renders if the value is truthy.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
{`{{#data.rating}}
Rating: {{data.rating}}
{{/data.rating}}`}
                      </Box>
                    </Box>

                    {/* Inverted sections */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Inverted Sections (Falsy / Missing)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Use <code>{'{{^variable}}...{{/variable}}'}</code> to render content only when
                        the value is missing, null, or false.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
{`{{^data.contact.website}}
No website provided.
{{/data.contact.website}}`}
                      </Box>
                    </Box>

                    {/* Arrays */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Arrays (Iteration)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Use a section on an array to iterate. Use <code>{'{{.}}'}</code> for the current item
                        in a simple string/number array.
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
{`Lines of business:
{{#data.linesOfBusiness}}
- {{.}}
{{/data.linesOfBusiness}}`}
                      </Box>
                    </Box>

                    <Divider />

                    {/* Full example */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Full Example (Insurance Schema)
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        A complete system prompt using multiple Mustache features:
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.900',
                          color: 'grey.100',
                          p: 1.5,
                          borderRadius: 1,
                          fontSize: '0.85rem',
                          overflow: 'auto',
                        }}
                      >
{`You are an insurance {{entityType}} processing agent.

{{#data}}
Entity: {{name}} (ID: {{insurerId}}{{brokerId}})
License: {{licenseNumber}} | Status: {{status}}

{{#contact}}
Contact: {{email}} | {{phone}}
{{#website}}Website: {{website}}{{/website}}
{{/contact}}

{{#address}}
Address: {{line1}}, {{city}}, {{state}} {{postalCode}}, {{country}}
{{/address}}

{{#linesOfBusiness}}
Lines of Business:
{{#linesOfBusiness}}- {{.}}
{{/linesOfBusiness}}
{{/linesOfBusiness}}
{{/data}}

Process the entity data according to the instructions.`}
                      </Box>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setHelpOpen(false)} variant="contained">
                    Close
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 2, pt: 2 }}>
                <Button variant="contained" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outlined" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
